import { animate, useMotionValue, useTransform } from 'motion/react'
import { motion } from '~/lib/motion'
import { useEffect, useRef } from 'react'
import { useStore } from '@nanostores/react'
import coinIcon from '~/features/reward-flow/assets/coin.svg'
import gemIcon from '~/features/reward-flow/assets/gem.svg'
import { useBalanceQuery } from '~/features/reward-flow/hooks/use-balance-query'
import { $rewardFlowState } from '../store'

export function BalanceBar() {
  const { isLoading, data: balanceData } = useBalanceQuery()

  const state = useStore($rewardFlowState)
  const items = state.items
  const currentIndex = state.currentIndex

  const balanceRef = useRef<{ coin: number; gem: number }>({
    coin: balanceData?.coin ?? 0,
    gem: balanceData?.gem ?? 0,
  })

  const coinMV = useMotionValue<number>(balanceRef.current.coin)
  const gemMV = useMotionValue<number>(balanceRef.current.gem)

  const coin = useTransform(coinMV, (val) => val.toFixed(0))
  const gem = useTransform(gemMV, (val) => val.toFixed(0))

  useEffect(() => {
    if (!items) return

    const rewards = items.slice(0, currentIndex + 1)
    const targetCoin =
      (balanceData?.coin ?? 0) +
      rewards
        .filter((i) => i.type === 'coin')
        .reduce((sum, i) => sum + i.value, 0)
    const targetGem =
      (balanceData?.gem ?? 0) +
      rewards
        .filter((i) => i.type === 'gem')
        .reduce((sum, i) => sum + i.value, 0)

    const ease = [0.33, 1, 0.68, 1] as const

    animate(coinMV, targetCoin, { duration: 1, ease, delay: 0.2 })
    animate(gemMV, targetGem, { duration: 1, ease, delay: 0.2 })
  }, [items, currentIndex, balanceData, coinMV, gemMV])

  return (
    <div className="flex shrink-0 gap-2">
      <div className="flex h-7 items-center gap-1 rounded-full bg-black/20 px-[5px] text-white">
        <img className="size-5" src={coinIcon} alt="coin" />

        {isLoading ? (
          <SpinnerIcon />
        ) : (
          <motion.span className="font-light">{coin}</motion.span>
        )}
      </div>

      <div className="flex h-7 items-center gap-1 rounded-full bg-black/20 px-[5px] text-white">
        <img className="size-5" src={gemIcon} alt="gem" />

        {isLoading ? (
          <SpinnerIcon />
        ) : (
          <motion.span className="font-light">{gem}</motion.span>
        )}
      </div>
    </div>
  )
}

function SpinnerIcon() {
  return (
    <svg
      className="size-4 animate-spin text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  )
}
